/**
 * Audio leader election, per event.
 *
 * Background (root cause of "Blind Up sound fires ~10s late"):
 *   Each timer tab has its own 1s setInterval. When a tab is hidden, Chrome
 *   throttles that interval, so the hidden tab's level-up transition fires
 *   late. The wall-clock countdown still looks correct (it uses Date.now()),
 *   but the level-up SOUND fires seconds after the real boundary — or fires
 *   again on the hidden tab even though the visible tab already played it.
 *
 * Solution:
 *   Only ONE tab per event is the "audio leader" and may play sounds. The
 *   leadership tracks the most-recently-activated visible tab, falling back to
 *   "most recently opened" when no tab is visible. Tabs cross-broadcast a
 *   heartbeat; if the leader goes away (closed/crashed), the remaining tab
 *   with the highest priority takes over.
 *
 * Keyed by eventId so two tabs of DIFFERENT events each keep their own leader.
 */

const HEARTBEAT_INTERVAL_MS = 1000
const LEASE_TTL_MS = 3000 // leader considered dead after this without heartbeat

const channels = new Map() // eventId -> ElectionState

/**
 * @typedef {Object} ElectionState
 * @property {string} eventId
 * @property {BroadcastChannel} channel
 * @property {string} tabId                 unique per tab instance
 * @property {number} priority              monotonically increasing; newest claim wins
 * @property {ReturnType<typeof setInterval>} heartbeatTimer
 * @property {ReturnType<typeof setInterval>} reapTimer
 * @property {Map<string, {priority:number, lastSeen:number, visible:boolean}>} peers  tabId -> info
 * @property {boolean} isLeader
 * @property {(isLeader:boolean) => void} [onLeadershipChange]
 */

const randomTabId = () =>
	`tab_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

/**
 * Decide who the leader is from the current peer table + self.
 * Highest priority wins; ties broken by tabId for determinism.
 * @returns {string|null}
 */
const computeLeader = (state) => {
	const candidates = []

	// Self
	candidates.push({
		tabId: state.tabId,
		priority: state.priority,
		lastSeen: Date.now(),
		visible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
	})

	// Peers that are still alive (heartbeat within TTL)
	const now = Date.now()
	for (const [tabId, info] of state.peers) {
		if (now - info.lastSeen < LEASE_TTL_MS) {
			candidates.push({ tabId, ...info })
		}
	}

	if (candidates.length === 0) return null

	// Prefer visible tabs; among those, highest priority; tie -> tabId
	const visible = candidates.filter((c) => c.visible)
	const pool = visible.length > 0 ? visible : candidates
	pool.sort((a, b) => {
		if (b.priority !== a.priority) return b.priority - a.priority
		return a.tabId < b.tabId ? -1 : 1
	})
	return pool[0]?.tabId ?? null
}

const recomputeAndNotify = (state) => {
	const leaderId = computeLeader(state)
	const wasLeader = state.isLeader
	state.isLeader = leaderId === state.tabId
	if (wasLeader !== state.isLeader && state.onLeadershipChange) {
		state.onLeadershipChange(state.isLeader)
	}
}

/**
 * Start participating in leader election for an event.
 * @param {string} eventId
 * @param {(isLeader:boolean) => void} [onLeadershipChange]
 * @returns {{
 *   isLeader: () => boolean,
 *   claim: () => void,            re-assert leadership (e.g. tab became visible)
 *   release: () => void,           stop participating (cleanup)
 * }}
 */
export const joinElection = (eventId, onLeadershipChange) => {
	if (!eventId) {
		throw new Error('joinElection: eventId is required')
	}
	if (typeof BroadcastChannel === 'undefined') {
		// Browser without BroadcastChannel (very old): behave as sole leader.
		const stub = { isLeader: () => true, claim: () => {}, release: () => {} }
		return stub
	}

	// Reuse an existing election for this event (e.g. HMR / StrictMode double-mount).
	const existing = channels.get(eventId)
	if (existing) {
		existing.onLeadershipChange = onLeadershipChange
		return {
			isLeader: () => existing.isLeader,
			claim: () => broadcastClaim(existing),
			release: () => teardown(existing),
		}
	}

	const state = /** @type {ElectionState} */ ({
		eventId,
		channel: new BroadcastChannel(`timer-audio:${eventId}`),
		tabId: randomTabId(),
		priority: Date.now(),
		peers: new Map(),
		isLeader: false,
		heartbeatTimer: null,
		reapTimer: null,
		onLeadershipChange,
	})
	channels.set(eventId, state)

	state.channel.onmessage = (event) => {
		const msg = event.data
		if (!msg || msg.eventId !== eventId) return

		if (msg.type === 'claim' || msg.type === 'heartbeat') {
			// A peer with a higher priority than us means we are NOT the newest;
			// adopt a priority just above theirs so future ties resolve correctly.
			if (msg.priority > state.priority) {
				state.priority = msg.priority + 1
			}
			state.peers.set(msg.tabId, {
				priority: msg.priority,
				lastSeen: Date.now(),
				visible: msg.visible,
			})
			recomputeAndNotify(state)
		} else if (msg.type === 'depart') {
			state.peers.delete(msg.tabId)
			recomputeAndNotify(state)
		}
	}

	const broadcastClaim = (s) => {
		s.priority = Math.max(s.priority, Date.now())
		s.channel.postMessage({
			type: 'claim',
			eventId,
			tabId: s.tabId,
			priority: s.priority,
			visible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
		})
		recomputeAndNotify(s)
	}

	const broadcastHeartbeat = () => {
		state.channel.postMessage({
			type: 'heartbeat',
			eventId,
			tabId: state.tabId,
			priority: state.priority,
			visible: typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
		})
		// Also refresh our own lastSeen so we don't reap ourselves under clock skew.
		// (Not strictly required since we always include self in computeLeader.)
		recomputeAndNotify(state)
	}

	const reap = () => {
		const now = Date.now()
		let changed = false
		for (const [tabId, info] of state.peers) {
			if (now - info.lastSeen >= LEASE_TTL_MS) {
				state.peers.delete(tabId)
				changed = true
			}
		}
		if (changed) recomputeAndNotify(state)
	}

	// Announce ourselves and start heartbeat.
	broadcastClaim(state)
	state.heartbeatTimer = setInterval(broadcastHeartbeat, HEARTBEAT_INTERVAL_MS)
	state.reapTimer = setInterval(reap, HEARTBEAT_INTERVAL_MS)

	return {
		isLeader: () => state.isLeader,
		claim: () => broadcastClaim(state),
		release: () => teardown(state),
	}
}

const teardown = (state) => {
	if (state.heartbeatTimer) clearInterval(state.heartbeatTimer)
	if (state.reapTimer) clearInterval(state.reapTimer)
	state.heartbeatTimer = null
	state.reapTimer = null
	try {
		state.channel.postMessage({
			type: 'depart',
			eventId: state.eventId,
			tabId: state.tabId,
		})
	} catch {
		// channel may already be closed
	}
	try {
		state.channel.close()
	} catch {
		// ignore
	}
	if (channels.get(state.eventId) === state) {
		channels.delete(state.eventId)
	}
	state.isLeader = false
	state.peers.clear()
}

/**
 * React hook convenience: subscribe to leadership for an event.
 * Returns a ref-like object; call `.current` to read leader status, or pass a
 * callback via onLeadershipChange to react to changes.
 *
 * NOTE: kept framework-agnostic on purpose so it can be unit-tested without React.
 */
export const _internal = { computeLeader, LEASE_TTL_MS }
